import { useTranslation } from 'react-i18next';

import { Pagination } from '@/components/data/Pagination';
import { DEFAULT_PAGE_SIZE } from '@/shared/constants/pagination';

import { useAdminsQuery } from '../hooks/use-admins';
import { useAdminListStore } from '../store/admin-list.store';
import type { AdminStatus } from '../types/admin.types';
import {
  AdminListEmpty,
  AdminListSkeleton,
  AdminListTable,
} from './AdminListTable';

function AdminListError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('admin');
  return (
    <div
      role="alert"
      style={{
        padding: 16,
        border: '1px solid var(--destructive)',
        borderRadius: 8,
        background: '#FEE2E8',
        color: 'var(--destructive)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
      }}
    >
      <span>{t('adminManagement.list.loadError')}</span>
      <button type="button" className="btn btn-sm btn-outline" onClick={onRetry}>
        {t('adminManagement.actions.retry')}
      </button>
    </div>
  );
}

export function AdminListView() {
  const filters = useAdminListStore((s) => s.filters);
  const sort = useAdminListStore((s) => s.sort);
  const page = useAdminListStore((s) => s.page);
  const setPage = useAdminListStore((s) => s.setPage);

  // Empty filter strings are omitted from the wire so the mock/HTTP layer
  // never receives '' as a filter (treated as "All").
  const params = {
    role: filters.role || undefined,
    status: (filters.status || undefined) as AdminStatus | undefined,
    q: filters.search.trim() || undefined,
    page,
    perPage: DEFAULT_PAGE_SIZE,
    sortBy: sort.key,
    sortDir: sort.dir,
  };

  const { data, isLoading, error, refetch } = useAdminsQuery(params);

  if (isLoading) return <AdminListSkeleton />;
  if (error) return <AdminListError onRetry={() => void refetch()} />;
  if (!data || data.items.length === 0) return <AdminListEmpty />;

  const totalPages = Math.max(1, Math.ceil(data.total / data.perPage));

  return (
    <>
      <AdminListTable admins={data.items} />
      <Pagination page={data.page} totalPages={totalPages} onPageChange={setPage} />
    </>
  );
}
